import { promoContract } from '@daymade/api-contracts';
import * as Sentry from '@sentry/browser';
import { ClientInferResponseBody } from '@ts-rest/core';
import GenericModal from 'components/GenericModal';
import { AdvertisedOnSection } from 'components/home-page/advertised-on/AdvertisedOnSection';
import { FaqSection } from 'components/home-page/faq/FaqSection';
import { HomePageFooter } from 'components/home-page/footer/Homepage-Footer';
import { HeroSection } from 'components/home-page/hero-section/HeroSection';
import { HowItWorksSection } from 'components/home-page/how-it-works/HowItWorksSection';
import { PrizesSection } from 'components/home-page/prize-section/PrizesSection';
import { TrustpilotSection } from 'components/home-page/trustpilot-section/TrustpilotSection';
import { WinnerSection } from 'components/home-page/winner-section/WinnerSection';
import MainLayout from 'components/layout/MainLayout';
import { REFERRAL_ID_QUERY_STRING_PARAMETER } from 'components/referrals/2026/ReferralConstants';
import RoundedButton from 'components/RoundedButton';
import { MainDrawTypeCode } from 'constants/Draw';
import { useEnterStore } from 'hooks/stores/useEnterStore';
import { useUIStore } from 'hooks/stores/useUIStore';
import { useUserStore } from 'hooks/stores/useUserStore';
import { observer } from 'mobx-react-lite';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import { NextSeo } from 'next-seo';
import { useEffect, useMemo, useState } from 'react';
import {
  getFirstEntryVsFirstOrder50Off2026Value,
  getHomePageHeroTextSep2025Value,
} from 'utils/ab-test/abTests';
import { getCurrentHeroText } from 'utils/ab-test/homepage/HeroTextTest';
import { tryApi } from 'utils/api/common';
import { drawApiClient, promoApiClient } from 'utils/apiClient';
import { firstInArrayOrValue } from 'utils/common';
import HttpStatusCode from 'utils/HttpStatusCode';
import { fetchWinnersHomepage, WinnerHomepage } from 'utils/plasmicCMS';

type GetPromoResponse = ClientInferResponseBody<
  typeof promoContract.public.getPromo,
  200
>;
export interface HomePageProps {
  nextDrawDate: string;
  winners: WinnerHomepage[];
  referralInfo: {
    referrerFirstName: string | null;
    referrerAvatarLocation: string | null;
    referralErrorMessageForUser: string | null;
  };
}

const HomePage = observer(
  ({
    nextDrawDate,
    winners,
    referralInfo,
  }: InferGetServerSidePropsType<typeof getServerSideProps>) => {
    const { activeSubscriptions } = useUserStore();
    const enterStore = useEnterStore();
    const uiStore = useUIStore();
    const [modalText, setModalText] = useState(
      referralInfo.referralErrorMessageForUser,
    );

    const isSubscriber = useMemo(
      () => activeSubscriptions.length > 0,
      [activeSubscriptions],
    );

    const [currentHeroText, setCurrentHeroText] = useState<
      ReturnType<typeof getCurrentHeroText>
    >(getCurrentHeroText('CONTROL'));

    useEffect(() => {
      if (!referralInfo.referrerFirstName) {
        // Don't assign a home page test group if we're referring a friend
        const testGroup = getHomePageHeroTextSep2025Value();
        setCurrentHeroText(getCurrentHeroText(testGroup));
        getFirstEntryVsFirstOrder50Off2026Value();
      }
    }, []);
    useEffect(() => {
      // Only show the toast if there is a promo message error, and if the promo message error has changed since the
      // page was loaded
      if (
        enterStore.promoErrorMessage &&
        referralInfo.referralErrorMessageForUser !==
          enterStore.promoErrorMessage
      ) {
        uiStore.displayToast({
          title: enterStore.promoErrorMessage,
          color: 'red',
          timeout: 6000,
        });
      }
    }, [enterStore.promoErrorMessage, uiStore]);

    // Only show the referral info if we're still loading. If, after loading, the referral is no longer valid,
    // then we should no longer show the referral in
    const referralInfoToRender =
      !enterStore.initialised || enterStore.isReferralFlow
        ? referralInfo
        : {
            referrerFirstName: null,
            referrerAvatarLocation: null,
            referralErrorMessageForUser: null,
          };

    return (
      <MainLayout limitItemWidths>
        <NextSeo
          title="Win Prizes | Win Holidays, Big Prizes & Experiences | DAYMADE"
          description="Play with DAYMADE for your chance to win holidays online. Become a winner from the comfort of your home. Find out about us, our prizes, and what we do here."
          openGraph={{
            title:
              'Win Prizes | Win Holidays, Big Prizes & Experiences | DAYMADE',
            description:
              'Play with DAYMADE for your chance to win holidays online. Become a winner from the comfort of your home. Find out about us, our prizes, and what we do here.',
          }}
        />

        <div className="aboutUsTypographyReset">
          {/* Hero Section */}
          <HeroSection
            heroText={currentHeroText}
            nextDrawDate={nextDrawDate}
            isSubscriber={isSubscriber}
          />

          {/* As advertised on */}
          <AdvertisedOnSection />

          {/* Prizes Section */}
          <PrizesSection isSubscriber={isSubscriber} />

          <WinnerSection winners={winners} isSubscriber={isSubscriber} />

          <HowItWorksSection
            isSubscriber={isSubscriber}
            referrerFirstName={
              referralInfoToRender.referrerFirstName ?? undefined
            }
            referrerAvatarLocation={
              referralInfoToRender.referrerAvatarLocation ?? undefined
            }
          />

          {/* Trustpilot Section */}
          <TrustpilotSection />

          {/* FAQ Section */}
          <FaqSection />

          {/* Win Prizes, Plant Trees */}
          <HomePageFooter
            isSubscriber={isSubscriber}
            referrerFirstName={
              referralInfoToRender.referrerFirstName ?? undefined
            }
          />
        </div>
        {modalText && (
          <GenericModal onClose={() => setModalText('')}>
            <p>{modalText}</p>
            <RoundedButton rebrandDm2025 onClick={() => setModalText('')}>
              Continue to Daymade
            </RoundedButton>
          </GenericModal>
        )}
      </MainLayout>
    );
  },
);

export const getServerSideProps: GetServerSideProps<HomePageProps> = async (
  ctx,
) => {
  const referralId = firstInArrayOrValue(
    ctx.query[REFERRAL_ID_QUERY_STRING_PARAMETER],
  );
  const [resp, winners, referralResponse] = await Promise.all([
    tryApi(
      drawApiClient.public.getDrawTypeCached({
        params: { draw_code: MainDrawTypeCode },
      }),
    ),
    fetchWinnersHomepage(),
    referralId
      ? tryApi(
          promoApiClient.public.getPromo({ body: { referralId: referralId } }),
        )
      : undefined,
  ]);

  let nextDrawDate: string = new Date().toISOString();

  if (resp.status === HttpStatusCode.OK) {
    if (resp.body.next_draw?.drawDate) {
      nextDrawDate = new Date(resp.body.next_draw.drawDate).toISOString();
    }
  }
  let referralInfo: undefined | GetPromoResponse;
  let referralErrorMessageForUser: string | undefined;
  if (referralResponse) {
    if (referralResponse?.status === HttpStatusCode.OK) {
      referralInfo = referralResponse.body;
    } else {
      referralInfo = undefined;
      if (referralResponse?.status === 'error') {
        Sentry.captureException(
          new (Error as any)(
            'Failed to retrieve referral code for ' + referralId,
            { cause: referralResponse.error },
          ),
        );
        referralErrorMessageForUser =
          'Sorry, there was an error retrieving the referral offer. Please try again later';
      } else {
        referralErrorMessageForUser = referralResponse.body.message;
      }
    }
  } else {
    referralInfo = undefined;
    referralErrorMessageForUser = undefined;
  }

  ctx.res.setHeader(
    'Cache-Control',
    'public, max-age=60, s-maxage=60, stale-while-revalidate=59',
  );

  return {
    props: {
      nextDrawDate,
      winners,
      referralInfo: {
        referrerFirstName: referralInfo?.referralDetails?.firstName ?? null,
        referrerAvatarLocation:
          referralInfo?.referralDetails?.avatarLocation ?? null,
        referralErrorMessageForUser: referralErrorMessageForUser ?? null,
      },
    },
  };
};

export default HomePage;
